'use client';

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Mail, X } from 'lucide-react';
import { m, type Variants } from 'motion/react';
import { css, cx } from 'styled-system/css';
import { siteSidebar } from 'styled-system/recipes';

import Connect2Wallet from '@shaojiang/components/Connect2Wallet';
import { useDesignMotion } from '@shaojiang/components/motion';
import { Button } from '@shaojiang/components/ui/button';
import { Drawer } from '@shaojiang/components/ui/drawer';
import { IconButton } from '@shaojiang/components/ui/icon-button';
import type { SiteCopy } from '@shaojiang/content';
import { POSES } from '@shaojiang/data/site';
import { DesignOnly } from '@shaojiang/designs/runtime/DesignOnly';
import useLocalizedHref from '@shaojiang/hooks/useLocalizedHref';
import type { Tone } from '@shaojiang/theme/preset/recipes/site/tones';

import { Copy, Emph } from './Copy';
import { LanguageToggle } from './LanguageToggle';
import { isActiveItem, type NavItem } from './nav-items';

interface SiteSidebarProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: NavItem[];
  path: string;
  brand: React.ReactNode;
  copy: SiteCopy['nav'];
  email: string;
}

/** Storybook's colored icon tiles (written out so Panda can extract them) */
const TILES: Record<Tone, string> = {
  sky: css({ bg: 'tone.sky.bg', color: 'tone.sky.fg' }),
  sun: css({ bg: 'tone.sun.bg', color: 'tone.sun.fg' }),
  leaf: css({ bg: 'tone.leaf.bg', color: 'tone.leaf.fg' }),
  blush: css({ bg: 'tone.blush.bg', color: 'tone.blush.fg' }),
  cobalt: css({ bg: 'tone.cobalt.bg', color: 'tone.cobalt.fg' }),
  tomato: css({ bg: 'tone.tomato.bg', color: 'tone.tomato.fg' }),
};

/** Items slide in one after another once the panel is out, and leave together. */
const itemVariants: Variants = {
  closed: { opacity: 0, x: -24 },
  open: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: { delay: 0.12 + i * 0.05, type: 'spring', stiffness: 380, damping: 28 },
  }),
};

/**
 * The site's sections in a panel that slides in from the left, for screens narrower than `lg`.
 * Opened from the menu button in the top bar; closes on navigation, Escape or a tap outside.
 */
export function SiteSidebar({
  open,
  onOpenChange,
  items,
  path,
  brand,
  copy,
  email,
}: SiteSidebarProps) {
  const classes = siteSidebar();
  const localize = useLocalizedHref();
  const motion = useDesignMotion();
  const close = () => onOpenChange(false);
  const animate = open ? 'open' : 'closed';

  return (
    <Drawer.Root variant='left' open={open} onOpenChange={(details) => onOpenChange(details.open)}>
      <Drawer.Backdrop />
      <Drawer.Positioner className={classes.positioner}>
        <Drawer.Content className={classes.panel} aria-label={copy.menu}>
          <div className={classes.header}>
            {brand}
            <Drawer.CloseTrigger asChild>
              <IconButton variant='surface' size='sm' aria-label={copy.closeMenu}>
                <X />
              </IconButton>
            </Drawer.CloseTrigger>
          </div>

          <DesignOnly design='storybook'>
            <m.p
              className={css({ textStyle: 'hand', color: 'fg.hot', px: '2.5', mb: '-3' })}
              custom={0}
              variants={itemVariants}
              initial='closed'
              animate={animate}
            >
              {copy.whereTo}
            </m.p>
          </DesignOnly>

          <nav aria-label={copy.menu}>
            <ul className={classes.list}>
              {items.map((item, i) => {
                const active = isActiveItem(item, path);
                const Icon = item.icon;
                return (
                  <m.li
                    key={item.href}
                    custom={i + 1}
                    variants={itemVariants}
                    initial='closed'
                    animate={animate}
                  >
                    <Link
                      href={localize(item.href)}
                      onClick={close}
                      aria-current={active ? 'page' : undefined}
                      className={cx('group', classes.item)}
                    >
                      {active && (
                        <m.span
                          layoutId='sidebar-indicator'
                          className={classes.indicator}
                          transition={motion.layout}
                        />
                      )}
                      <span className={cx(classes.icon, TILES[item.tone])}>
                        <Icon />
                      </span>
                      <span className={classes.label}>
                        <Copy text={item.label} />
                      </span>
                    </Link>
                    {item.children && (
                      <ul className={classes.sub}>
                        {item.children.map((child) => {
                          const ChildIcon = child.icon;
                          return (
                            <li key={child.href}>
                              <Link
                                href={localize(child.href)}
                                onClick={close}
                                aria-current={path === child.href ? 'page' : undefined}
                                className={classes.subItem}
                              >
                                <ChildIcon />
                                <Emph>{child.label.replace(/\*/g, '')}</Emph>
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </m.li>
                );
              })}
            </ul>
          </nav>

          <m.div
            className={classes.footer}
            custom={items.length + 1}
            variants={itemVariants}
            initial='closed'
            animate={animate}
          >
            <DesignOnly design='storybook'>
              <Image
                src={POSES.glasses}
                alt=''
                width={120}
                height={120}
                className={css({ w: '24', h: '24', mx: 'auto', mb: '-3' })}
              />
            </DesignOnly>
            <div
              className={css({
                display: 'flex',
                alignItems: 'center',
                gap: '2',
                justifyContent: 'center',
              })}
            >
              <LanguageToggle label={copy.language} title={copy.otherLanguage} />
              <Connect2Wallet label={copy.wallet} />
            </div>
            <Button asChild variant='hot' className={css({ w: 'full' })}>
              <a href={`mailto:${email}`} onClick={close}>
                <Mail />
                <Copy text={copy.cta} />
              </a>
            </Button>
          </m.div>
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  );
}
